import { useEffect, useState } from 'react';
import {
  defaultSalonSettings,
  newSalonId,
  DAY_LABELS,
  WEEK_DAYS_ORDER,
  type SalonSettings,
  type WeekDay
} from '@/lib/salonSettings';
import { fetchTenantBusiness, saveTenantBusiness } from '@/lib/tenantBusiness';
import { GalleryEditor } from './GalleryEditor';

// Редактор салона в кабинете владельца: контакты, график, услуги, мастера.
// Владелец меняет данные сам — витрина и запись подхватывают правки.
// Прод: те же поля уходят в D1 (tenants.business, services, staff).
export function SalonEditor() {
  const [draft, setDraft] = useState<SalonSettings>(() => defaultSalonSettings());
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTenantBusiness('current').then(setDraft).catch(() => setMsg('Не удалось загрузить настройки. Показан локальный черновик.')).finally(() => setLoading(false));
  }, []);

  const patch = (p: Partial<SalonSettings>) => {
    setDraft((d) => ({ ...d, ...p }));
    setMsg(null);
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setMsg('Название салона не может быть пустым.');
      return;
    }
    const badService = draft.services.find(
      (s) => !s.name.trim() || s.price < 0 || s.durationMinutes <= 0
    );
    if (badService) {
      setMsg('У услуги проверьте название, цену и длительность.');
      return;
    }
    try {
      setSaving(true);
      await saveTenantBusiness('current', draft);
      setMsg('Сохранено. Витрина и запись уже показывают новые данные.');
    } catch (cause) {
      setMsg(cause instanceof Error ? cause.message : 'Не удалось сохранить настройки.');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      setDraft(await fetchTenantBusiness('current'));
      setMsg('Форма вернулась к последнему сохранённому виду.');
    } catch { setMsg('Не удалось повторно загрузить настройки.'); }
  };

  // --- услуги ---
  const updateService = (id: string, p: Partial<SalonSettings['services'][number]>) => {
    patch({
      services: draft.services.map((s) => (s.id === id ? { ...s, ...p } : s))
    });
  };
  const addService = () => {
    patch({
      services: [
        ...draft.services,
        { id: newSalonId('svc'), name: 'Новая услуга', price: 1000, durationMinutes: 60, description: '', popular: false }
      ]
    });
  };
  const removeService = (id: string) => {
    patch({ services: draft.services.filter((s) => s.id !== id) });
  };

  // --- мастера ---
  const updateStaff = (id: string, p: Partial<SalonSettings['staff'][number]>) => {
    patch({ staff: draft.staff.map((m) => (m.id === id ? { ...m, ...p } : m)) });
  };
  const addStaff = () => {
    patch({ staff: [...draft.staff, { id: newSalonId('staff'), name: 'Новый мастер', role: '' }] });
  };
  const removeStaff = (id: string) => {
    patch({ staff: draft.staff.filter((m) => m.id !== id) });
  };

  // --- график ---
  const updateDay = (day: WeekDay, value: { open: string; close: string } | null) => {
    patch({ schedule: { ...draft.schedule, [day]: value } });
  };

  return (
    <>
      {loading && <p className="mnc-muted">Загружаем настройки…</p>}

      <section className="mnc-step">
        <h2>
          <span className="mnc-step-num">О</span>
          О салоне
        </h2>
        <p className="mnc-muted">Эти данные видят клиенты на витрине и в подтверждении записи.</p>

        <label className="mnc-field">
          Название
          <input value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
        </label>
        <label className="mnc-field">
          Слоган
          <input value={draft.tagline ?? ''} onChange={(e) => patch({ tagline: e.target.value })} />
        </label>
        <label className="mnc-field">
          Телефон
          <input
            inputMode="tel"
            placeholder="+7 999 123-45-67"
            value={draft.phone ?? ''}
            onChange={(e) => patch({ phone: e.target.value })}
          />
        </label>
        <label className="mnc-field">
          Адрес
          <input value={draft.address ?? ''} onChange={(e) => patch({ address: e.target.value })} />
        </label>
        <label className="mnc-field">
          Как найти
          <input
            value={draft.addressHint ?? ''}
            onChange={(e) => patch({ addressHint: e.target.value })}
          />
        </label>
        <label className="mnc-field">
          Ссылка на карту
          <input value={draft.mapUrl ?? ''} onChange={(e) => patch({ mapUrl: e.target.value })} />
        </label>
        <label className="mnc-field">
          О салоне
          <input value={draft.about ?? ''} onChange={(e) => patch({ about: e.target.value })} />
        </label>
      </section>

      <section className="mnc-step">
        <h2>
          <span className="mnc-step-num">Г</span>
          График работы
        </h2>
        <ul className="mnc-list">
          {WEEK_DAYS_ORDER.map((day) => {
            const hours = draft.schedule[day] ?? null;
            return (
              <li key={day}>
                <span style={{ minWidth: 34 }}>{DAY_LABELS[day]}</span>
                <span className="mnc-owner-row">
                  {hours !== null ? (
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="time"
                        aria-label={`${DAY_LABELS[day]}, начало работы`}
                        style={{ width: 110, margin: 0 }}
                        value={hours.open}
                        onChange={(e) => updateDay(day, { open: e.target.value, close: hours.close })}
                      />
                      <span className="mnc-muted">—</span>
                      <input
                        type="time"
                        aria-label={`${DAY_LABELS[day]}, окончание работы`}
                        style={{ width: 110, margin: 0 }}
                        value={hours.close}
                        onChange={(e) => updateDay(day, { open: hours.open, close: e.target.value })}
                      />
                    </span>
                  ) : (
                    <span className="mnc-muted">Выходной</span>
                  )}
                </span>
                <button
                  className="mnc-linklike"
                  onClick={() => updateDay(day, hours !== null ? null : { open: '10:00', close: '20:00' })}
                >
                  {hours !== null ? 'Выходной' : 'Рабочий'}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="mnc-step">
        <h2>
          <span className="mnc-step-num">У</span>
          Услуги
        </h2>
        <p className="mnc-muted">Цена и длительность сразу видны клиенту при записи.</p>
        {draft.services.map((s) => (
          <div className="mnc-editor-row" key={s.id}>
            <label className="mnc-field">
              Название
              <input value={s.name} onChange={(e) => updateService(s.id, { name: e.target.value })} />
            </label>
            <div className="mnc-editor-inline">
              <label className="mnc-field">
                Цена, ₽
                <input
                  type="number"
                  min={0}
                  value={s.price}
                  onChange={(e) => updateService(s.id, { price: Number(e.target.value) || 0 })}
                />
              </label>
              <label className="mnc-field">
                Длительность, мин
                <input
                  type="number"
                  min={5}
                  step={5}
                  value={s.durationMinutes}
                  onChange={(e) => updateService(s.id, { durationMinutes: Number(e.target.value) || 0 })}
                />
              </label>
            </div>
            <label className="mnc-field">
              Описание
              <input
                value={s.description ?? ''}
                onChange={(e) => updateService(s.id, { description: e.target.value })}
              />
            </label>
            <div className="mnc-actions" style={{ marginTop: 8 }}>
              <label className="mnc-check">
                <input
                  type="checkbox"
                  checked={Boolean(s.popular)}
                  onChange={(e) => updateService(s.id, { popular: e.target.checked })}
                />
                Часто выбирают
              </label>
              <button className="mnc-btn mnc-btn-ghost mnc-btn-sm" onClick={() => removeService(s.id)}>
                Удалить
              </button>
            </div>
          </div>
        ))}
        <button className="mnc-btn mnc-btn-ghost" onClick={addService}>
          Добавить услугу
        </button>
      </section>

      <section className="mnc-step">
        <h2>
          <span className="mnc-step-num">М</span>
          Мастера
        </h2>
        {draft.staff.map((m) => (
          <div className="mnc-editor-row" key={m.id}>
            <label className="mnc-field">
              Имя
              <input value={m.name} onChange={(e) => updateStaff(m.id, { name: e.target.value })} />
            </label>
            <label className="mnc-field">
              Специализация
              <input value={m.role ?? ''} onChange={(e) => updateStaff(m.id, { role: e.target.value })} />
            </label>
            <div className="mnc-actions" style={{ marginTop: 8 }}>
              <button className="mnc-btn mnc-btn-ghost mnc-btn-sm" onClick={() => removeStaff(m.id)}>
                Удалить
              </button>
            </div>
          </div>
        ))}
        <button className="mnc-btn mnc-btn-ghost" onClick={addStaff}>
          Добавить мастера
        </button>
      </section>

      <GalleryEditor settings={draft} onChange={(gallery) => patch({ gallery })} />

      <section className="mnc-step">
        <div className="mnc-actions">
          <button className="mnc-btn" disabled={saving || loading} onClick={save}>
            {saving ? 'Сохраняем…' : 'Сохранить изменения'}
          </button>
          <button className="mnc-btn mnc-btn-ghost" disabled={saving || loading} onClick={reset}>
            Отменить
          </button>
        </div>
        {msg && <p className="mnc-muted">{msg}</p>}
      </section>
    </>
  );
}
